import { useState } from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';

import { fFromNow } from 'src/utils/format-time';

import { CONFIG } from 'src/config-global';

// Get commit hash and version from environment or fallback
const getEnvironmentInfo = () => {
    const { environment, version, commitHash, branch, buildTime: _buildTime } = CONFIG.build;
    // Convert build time to timestamp
    const buildTime = _buildTime && !isNaN(_buildTime) ? _buildTime * 1000 : null;

    return {
        commitHash,
        environment,
        version,
        buildTime,
        branch,
    };
};

const getEnvironmentColor = (environment, theme) => {
    switch (environment) {
        case 'local':
            return {
                bg: theme.palette.info.lighter,
                text: theme.palette.info.darker,
                border: theme.palette.info.light,
            };
        case 'development':
            return {
                bg: theme.palette.warning.lighter,
                text: theme.palette.warning.darker,
                border: theme.palette.warning.light,
            };
        case 'staging':
            return {
                bg: theme.palette.success.lighter,
                text: theme.palette.success.darker,
                border: theme.palette.success.light,
            };
        case 'production':
            return {
                bg: theme.palette.error.lighter,
                text: theme.palette.error.darker,
                border: theme.palette.error.light,
            };
        default:
            return {
                bg: theme.palette.grey[100],
                text: theme.palette.grey[900],
                border: theme.palette.grey[300],
            };
    }
};


const TextLabel = ({ children, ...props }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 10, minWidth: 70 }} {...props}>
        {children}
    </Typography>
);

export function EnvironmentIndicator() {
    const theme = useTheme();
    const info = getEnvironmentInfo();
    const [copied, setCopied] = useState(false);
    const { copy } = useCopyToClipboard();

    const colors = getEnvironmentColor(info.environment, theme);

    const handleCopyCommit = async () => {
        await copy(info.commitHash);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const renderTooltip = () => (
        <Box
            sx={{
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: 'max-content',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '12px',
                }}
            >
                <TextLabel>ENV:</TextLabel>
                <Box sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    {info.environment}
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '12px',
                }}
            >
                <TextLabel>BRANCH:</TextLabel>
                <Box sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    {info.branch}
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '12px',
                }}
            >
                <TextLabel>COMMIT:</TextLabel>
                <Box
                    sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        cursor: 'pointer',
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5,
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                        },
                    }}
                >
                    {info.commitHash}
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '12px',
                }}
            >
                <TextLabel>VERSION:</TextLabel>
                <Box sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    v{info.version}
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '11px',
                    opacity: 0.85,
                    pt: 0.5,
                    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                }}
            >
                <TextLabel>BUILT:</TextLabel>
                <Box sx={{ fontFamily: 'monospace', fontWeight: 400 }}>
                    {new Date(info.buildTime).toLocaleString()}
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '11px',
                    opacity: 0.85,
                }}
            >
                <TextLabel>AGO:</TextLabel>
                <Box sx={{ fontFamily: 'monospace', fontWeight: 400 }}>
                    {fFromNow(info.buildTime)}
                </Box>
            </Box>

            <Box
                sx={{
                    fontSize: '11px',
                    opacity: 0.7,
                    pt: 0.5,
                    fontStyle: 'italic',
                }}
            >
                Click to copy commit hash
            </Box>
        </Box>
    );

    const tooltipText = renderTooltip();

    return (
        <Tooltip title={tooltipText} arrow placement="bottom">
            <Box
                component="button"
                onClick={handleCopyCommit}
                aria-label="Copy commit hash to clipboard"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    px: 1.5,
                    py: 0.5,
                    mx: 1,
                    borderRadius: 1,
                    bgcolor: colors.bg,
                    border: `1px solid ${colors.border}`,
                    cursor: 'pointer',
                    transition: theme.transitions.create(['all'], {
                        duration: theme.transitions.duration.shorter,
                    }),
                    '&:hover': {
                        bgcolor: colors.border,
                        boxShadow: theme.shadows[2],
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                    }}
                >
                    {/* Branch name */}
                    <Box
                        sx={{
                            fontSize: '10px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            color: colors.text,
                            letterSpacing: 0.5,
                            opacity: 0.8,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {info.branch}
                    </Box>

                    {/* Separator dot */}
                    <Box
                        sx={{
                            width: 3,
                            height: 3,
                            borderRadius: '50%',
                            bgcolor: colors.text,
                            opacity: 0.4,
                        }}
                    />

                    {/* Commit hash */}
                    <Box
                        sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            color: colors.text,
                            letterSpacing: 0.3,
                        }}
                    >
                        {copied ? '✓ Copied!' : info.commitHash}
                    </Box>
                </Box>

                {/* Environment indicator dot */}
                <Box
                    sx={{
                        ml: 0.75,
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: colors.text,
                        opacity: 0.6,
                    }}
                />
            </Box>
        </Tooltip>
    );
}
